import React, { useEffect, useMemo, useState } from 'react';
import type { ChecklistLinkedEntity, ChecklistResponse, FormType } from '@troia-v3/types';

import {
  Button,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  type SelectOption,
} from '@/shared/components/ui';
import { useFormsDropdown } from '@/shared/hooks/useFormsDropdown';
import { useUnitsDropdown } from '@/shared/hooks/useUnitsDropdown';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { DUE_UNIT_OPTIONS, splitDueMinutes, toDueMinutes, type DueUnit } from '@/shared/utils/dueMinutes';

import { useCreateChecklist } from '../hooks/useChecklists';

export interface ApplyChecklistTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Vínculo com lead/ticket. AUSENTE = checklist AVULSO (criado pela listagem
   * de gestão) — o payload sai sem `linkedEntity`, que é opcional no
   * `CreateChecklistRequest`.
   */
  linkedEntity?: ChecklistLinkedEntity;
  /**
   * Responsáveis já carregados pelo pai (aba do drawer — a lista é usada lá
   * também pra resolver `assigneeId → nome`). Quando ausente, o modal busca
   * por conta própria, gateado pelo `isOpen` (CLAUDE.md SEMPRE #10).
   */
  assigneeOptions?: SelectOption[];
  /**
   * Exibe o Select de Unidade (opcional no payload). Só faz sentido na
   * listagem de GESTÃO — dentro do drawer de um lead/ticket o contexto já é a
   * entidade, não a unidade física.
   */
  showUnitField?: boolean;
  /**
   * Trava o tipo de modelo e esconde o seletor "Checklist | Formulário". A
   * listagem de gestão só mostra aplicações de CHECKLIST — criar um formulário
   * por ela geraria um registro que some da própria tela.
   */
  lockedType?: FormType;
  title?: string;
  confirmLabel?: string;
  /** Prefixo dos `data-testid` (default `apply-template`). */
  testIdPrefix?: string;
  /** Chamado com a instância criada — usado pra navegar até o checklist novo. */
  onCreated?: (checklist: ChecklistResponse) => void;
}

/**
 * ApplyChecklistTemplateModal — cria uma instância de checklist a partir de um
 * modelo (`Form` com `type: 'checklist'` OU `'form'` — spec §5.5, os dois são
 * aplicáveis).
 *
 * Componente ÚNICO usado em dois pontos (feedback `ds_reference_exact_parity`
 * — estender com props opcionais, nunca duplicar):
 *  - `ChecklistEntityTab` (drawers de lead/ticket) → com `linkedEntity`
 *  - `ChecklistsPage` (listagem de gestão) → avulso, com Unidade
 */
export const ApplyChecklistTemplateModal: React.FC<ApplyChecklistTemplateModalProps> = ({
  isOpen,
  onClose,
  linkedEntity,
  assigneeOptions,
  showUnitField = false,
  lockedType,
  title = 'Aplicar modelo',
  confirmLabel = 'Aplicar',
  testIdPrefix = 'apply-template',
  onCreated,
}) => {
  const initialType: FormType = lockedType ?? 'checklist';
  const [templateType, setTemplateType] = useState<FormType>(initialType);
  const [templateId, setTemplateId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [dueMinutes, setDueMinutes] = useState<number | undefined>(undefined);
  /**
   * Unidade "lembrada" só para o caso de CAMPO VAZIO — com valor definido, a
   * unidade é 100% derivada de `dueMinutes` via `splitDueMinutes` logo abaixo.
   * Mesmo padrão do `CreateChecklistConfig.tsx` (node do workflow) e do
   * `FormBuilderPage.tsx` (prazo padrão do modelo) — os 3 pontos que editam
   * prazo em minutos+unidade se comportam igual.
   */
  const [emptyDueUnit, setEmptyDueUnit] = useState<DueUnit>('hours');

  const prazo = dueMinutes !== undefined
    ? splitDueMinutes(dueMinutes)
    : { value: undefined as number | undefined, unit: emptyDueUnit };

  const aplicarPrazo = (valorCru: string, unidade: DueUnit): void => {
    if (valorCru.trim() === '') {
      setEmptyDueUnit(unidade);
      setDueMinutes(undefined);
      return;
    }
    const n = parseInt(valorCru, 10);
    if (Number.isNaN(n)) {
      setEmptyDueUnit(unidade);
      setDueMinutes(undefined);
      return;
    }
    setDueMinutes(Math.min(Math.max(toDueMinutes(n, unidade), 1), 43_200));
  };

  // Reset ao fechar — senão reabre com a última seleção "fantasma" (DS §Overlays, ciclo pai→filho).
  useEffect(() => {
    if (!isOpen) {
      setTemplateType(initialType);
      setTemplateId('');
      setAssigneeId('');
      setUnitId('');
      setDueMinutes(undefined);
      setEmptyDueUnit('hours');
    }
  }, [isOpen, initialType]);

  // Só busca o type ativo do toggle — gated pelo próprio `isOpen` do modal (SEMPRE #10).
  const { data: checklistOptions } = useFormsDropdown('checklist', isOpen && templateType === 'checklist');
  const { data: formOptions } = useFormsDropdown('form', isOpen && templateType === 'form');
  const modelOptions: SelectOption[] = (templateType === 'checklist' ? checklistOptions : formOptions) ?? [];

  // Responsáveis: reusa a lista do pai quando ela existe (evita 2ª query idêntica).
  const { data: ownUserOptions } = useUsersDropdown({ enabled: isOpen && !assigneeOptions });
  const resolvedAssigneeOptions = useMemo<SelectOption[]>(
    () => assigneeOptions ?? (ownUserOptions ?? []).map((u) => ({ value: u.id, label: u.name })),
    [assigneeOptions, ownUserOptions],
  );

  const { data: unitOptions } = useUnitsDropdown(isOpen && showUnitField);

  const createMutation = useCreateChecklist();

  const handleTypeChange = (type: FormType) => {
    setTemplateType(type);
    setTemplateId('');
  };

  const handleConfirm = async () => {
    if (!templateId || !assigneeId) return;
    try {
      const created = await createMutation.mutateAsync({
        templateId,
        assigneeId,
        ...(dueMinutes !== undefined ? { dueMinutes } : {}),
        ...(showUnitField && unitId ? { unitId } : {}),
        ...(linkedEntity ? { linkedEntity } : {}),
      });
      onClose();
      onCreated?.(created);
    } catch {
      // Toast já disparado pelo onError do useCreateChecklist — mantém o modal aberto pra correção.
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md">
      <div data-testid={`${testIdPrefix}-modal`}>
        <ModalHeader title={title} onClose={onClose} />
        <ModalContent>
          <div className="space-y-4">
            {!lockedType && (
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                  Tipo de modelo
                </label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={templateType === 'checklist' ? 'primary' : 'secondary'}
                    size="sm"
                    fullWidth
                    onClick={() => handleTypeChange('checklist')}
                    data-testid={`${testIdPrefix}-type-checklist`}
                  >
                    Checklist
                  </Button>
                  <Button
                    type="button"
                    variant={templateType === 'form' ? 'primary' : 'secondary'}
                    size="sm"
                    fullWidth
                    onClick={() => handleTypeChange('form')}
                    data-testid={`${testIdPrefix}-type-form`}
                  >
                    Formulário
                  </Button>
                </div>
              </div>
            )}

            <Select
              label="Modelo"
              placeholder="Selecione um modelo"
              options={modelOptions}
              value={templateId}
              onChange={setTemplateId}
              data-testid={`${testIdPrefix}-model-select`}
            />

            <Select
              label="Responsável"
              placeholder="Selecione um responsável"
              options={resolvedAssigneeOptions}
              value={assigneeId}
              onChange={setAssigneeId}
              data-testid={`${testIdPrefix}-assignee-select`}
            />

            {showUnitField && (
              <Select
                label="Unidade (opcional)"
                placeholder="Sem unidade"
                options={unitOptions ?? []}
                value={unitId}
                onChange={setUnitId}
                data-testid={`${testIdPrefix}-unit-select`}
              />
            )}

            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                Prazo
              </label>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="number"
                  min={1}
                  step={1}
                  placeholder="Opcional"
                  value={prazo.value === undefined ? '' : String(prazo.value)}
                  onChange={(e) => aplicarPrazo(e.target.value, prazo.unit)}
                  data-testid={`${testIdPrefix}-due-value`}
                />
                <Select
                  value={prazo.unit}
                  onChange={(unidade) => aplicarPrazo(
                    prazo.value === undefined ? '' : String(prazo.value),
                    unidade as DueUnit,
                  )}
                  options={DUE_UNIT_OPTIONS}
                  data-testid={`${testIdPrefix}-due-unit`}
                />
              </div>
              {/* Espelha o Zod do backend (`int().min(1).max(43_200)`, 30 dias):
                  o clamp acontece em aplicarPrazo a cada tecla e o campo se
                  reescreve com o valor cortado — mesmo padrão do
                  CreateChecklistConfig.tsx e do FormBuilderPage.tsx (o corte
                  não fica escondido até o Aplicar). */}
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} data-testid={`${testIdPrefix}-cancel`}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={createMutation.isPending}
            disabled={!templateId || !assigneeId}
            onClick={handleConfirm}
            data-testid={`${testIdPrefix}-confirm`}
          >
            {confirmLabel}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

ApplyChecklistTemplateModal.displayName = 'ApplyChecklistTemplateModal';
